import { cn } from '@commenda/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';
import { Icons } from '../Icons';

const searchInputVariants = cva(
  'flex items-center gap-md rounded-md border-border-secondary bg-bg-primary transition-colors focus-within:border-fg-brand',
  {
    variants: {
      inputSize: {
        sm: 'h-7 border-1/2 px-lg',
        md: 'border px-lg py-md shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-fit',
      },
    },
    defaultVariants: {
      inputSize: 'md',
      fullWidth: true,
    },
  }
);

export type SearchInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> &
  VariantProps<typeof searchInputVariants>;

/** `className` lands on the bordered container, not the inner input, which is deliberately unstyled. */
export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  ({ inputSize, fullWidth, className, ...inputProps }, ref) => (
    <div
      className={cn(searchInputVariants({ inputSize, fullWidth }), className)}
    >
      <Icons.SearchSm
        className="size-4 shrink-0 text-text-placeholder"
        aria-hidden="true"
      />
      <input
        ref={ref}
        type="text"
        autoComplete="off"
        className="txt-small-regular min-w-0 flex-1 appearance-none border-0 bg-transparent p-none text-text-primary placeholder:text-text-placeholder focus:outline-none focus:ring-0"
        {...inputProps}
      />
    </div>
  )
);

SearchInput.displayName = 'SearchInput';
